import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { useI18n, withLang } from "@/i18n/I18nProvider";

const HASH_LINKS = [
  { key: "how", hash: "#como-funciona" },
  { key: "projects", hash: "#proyectos" },
  { key: "stats", hash: "#cifras" },
  { key: "contact", hash: "#contacto" },
];

export function Header() {
  const { t, lang } = useI18n();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const otherLang = lang === "ar" ? "en" : "ar";
  const switchLangPath = () => {
    // Preserve rest of path after language segment
    const parts = pathname.split("/").filter(Boolean);
    if (parts[0] === "ar" || parts[0] === "en") parts[0] = otherLang;
    else parts.unshift(otherLang);
    return "/" + parts.join("/");
  };

  const handleAnchor = (e, hash) => {
    e.preventDefault();
    setOpen(false);
    // If not on home for current lang, navigate to it first then scroll
    const homePath = withLang(lang);
    const doScroll = () => {
      const el = document.querySelector(hash);
      if (el && window.__lenis) {
        window.__lenis.scrollTo(el, { offset: -80 });
      } else if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };
    if (pathname !== homePath) {
      navigate(homePath);
      setTimeout(doScroll, 120);
    } else {
      doScroll();
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-[#0B132B]/85 border-b border-slate-800/70" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 lg:h-20 flex items-center justify-between">
        <Link to={withLang(lang)} className="flex items-center" data-testid="header-logo-link" aria-label="Zantravika">
          <Logo size={34} />
        </Link>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {HASH_LINKS.map((l) => (
            <a
              key={l.key}
              href={l.hash}
              onClick={(e) => handleAnchor(e, l.hash)}
              className="text-[13px] tracking-wide text-slate-300 hover:text-white transition-colors"
              data-testid={`nav-${l.key}-link`}
            >
              {t(`nav.${l.key}`)}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <div
            className="flex items-center rounded-full border border-slate-700/70 p-0.5"
            role="group"
            aria-label={t("nav.lang_label")}
          >
            <Link
              to={lang === "ar" ? pathname : switchLangPath()}
              className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest rounded-full transition ${
                lang === "ar" ? "bg-emerald-500 text-[#0B132B]" : "text-slate-300 hover:text-white"
              }`}
              data-testid="language-switcher-ar"
              onClick={(e) => { if (lang === "ar") e.preventDefault(); }}
            >
              AR
            </Link>
            <Link
              to={lang === "en" ? pathname : switchLangPath()}
              className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest rounded-full transition ${
                lang === "en" ? "bg-emerald-500 text-[#0B132B]" : "text-slate-300 hover:text-white"
              }`}
              data-testid="language-switcher-en"
              onClick={(e) => { if (lang === "en") e.preventDefault(); }}
            >
              EN
            </Link>
          </div>

          <a
            href="#contacto"
            onClick={(e) => handleAnchor(e, "#contacto")}
            className="btn-emerald text-sm"
            data-testid="header-cta-button"
          >
            {t("nav.cta")}
            <ArrowUpRight className="w-4 h-4" strokeWidth={2.2} />
          </a>
        </div>

        {/* Mobile toggle */}
        <button
          type="button"
          className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-slate-700/70 text-white"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? t("nav.close") : t("nav.menu")}
          aria-expanded={open}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-drawer"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden border-t border-slate-800/60 bg-[#0B132B]/95 backdrop-blur-xl"
            data-testid="mobile-drawer"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex flex-col gap-2">
              {HASH_LINKS.map((l) => (
                <a
                  key={l.key}
                  href={l.hash}
                  onClick={(e) => handleAnchor(e, l.hash)}
                  className="py-3 text-base border-b border-slate-800/70 text-slate-200"
                  data-testid={`mobile-nav-${l.key}-link`}
                >
                  {t(`nav.${l.key}`)}
                </a>
              ))}
              <div className="pt-4 flex items-center gap-3">
                <Link
                  to={lang === "ar" ? pathname : switchLangPath()}
                  className={`px-3 py-2 text-xs font-mono uppercase tracking-widest rounded-full border ${
                    lang === "ar" ? "bg-emerald-500 text-[#0B132B] border-emerald-500" : "border-slate-700 text-slate-300"
                  }`}
                  onClick={() => setOpen(false)}
                  data-testid="mobile-language-switcher-ar"
                >
                  AR
                </Link>
                <Link
                  to={lang === "en" ? pathname : switchLangPath()}
                  className={`px-3 py-2 text-xs font-mono uppercase tracking-widest rounded-full border ${
                    lang === "en" ? "bg-emerald-500 text-[#0B132B] border-emerald-500" : "border-slate-700 text-slate-300"
                  }`}
                  onClick={() => setOpen(false)}
                  data-testid="mobile-language-switcher-en"
                >
                  EN
                </Link>
                <a
                  href="#contacto"
                  onClick={(e) => handleAnchor(e, "#contacto")}
                  className="btn-emerald text-sm ml-auto"
                  data-testid="mobile-header-cta-button"
                >
                  {t("nav.cta")}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

export default Header;
